import { useState, useEffect, useRef } from 'react';
import { TESTIMONIALS } from '../../mocks/lisaMerkleData';
import { Link } from 'react-router-dom';

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => {
          setTimeout(() => node.classList.add('revealed'), i * 100);
        });
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const prev = () => setActive((v) => (v === 0 ? TESTIMONIALS.length - 1 : v - 1));
  const next = () => setActive((v) => (v === TESTIMONIALS.length - 1 ? 0 : v + 1));
  const t = TESTIMONIALS[active];

  return (
    <section ref={ref} className="bg-cream py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-8">

        {/* Header row */}
        <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Client Testimonials</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-charcoal mt-3 leading-tight">
              What Our Clients Say
            </h2>
          </div>
          <div className="flex items-center gap-6">
            {/* Rating badge */}
            <div className="flex flex-col items-center px-6 py-4 border border-stone/25 bg-white">
              <span className="font-display text-4xl font-light text-charcoal leading-none">4.9</span>
              <div className="flex gap-0.5 my-1.5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-stone text-xs" />
                ))}
              </div>
              <span className="text-charcoal/40 text-[10px] tracking-wider font-sans">Client Rating</span>
            </div>
          </div>
        </div>

        {/* Main testimonial grid */}
        <div className="grid lg:grid-cols-5 gap-0 border border-stone/20">
          {/* Left image panel */}
          <div className="reveal hidden lg:block lg:col-span-2 relative">
            <div className="h-full min-h-[520px] overflow-hidden">
              <img
                src="https://lisamerkledesign.com/wp-content/uploads/2022/12/210.png"
                alt="Lisa Merkle Design luxury interior"
                className="w-full h-full object-cover object-top transition-transform duration-1000 hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <p className="text-cream/70 text-xs font-sans leading-relaxed italic">
                "Designed for Beverly Hills' most discerning clients — from celebrity estates to family homes."
              </p>
            </div>
          </div>

          {/* Right testimonial panel */}
          <div className="reveal reveal-delay-2 lg:col-span-3 flex flex-col justify-between p-10 lg:p-14 bg-white">
            <div>
              <i className="ri-double-quotes-l text-stone/25 font-display" style={{ fontSize: '4rem' }} />
              <blockquote
                key={active}
                className="font-display text-xl md:text-2xl lg:text-3xl font-light text-charcoal leading-relaxed mt-2 mb-8"
                style={{ animation: 'fadeIn 0.4s ease forwards' }}
              >
                "{t.text}"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-charcoal rounded-full flex-shrink-0">
                  <span className="text-cream font-display text-lg font-medium">{t.initials}</span>
                </div>
                <div>
                  <p className="font-sans font-semibold text-charcoal text-sm">{t.name}</p>
                  <p className="text-charcoal/40 text-xs font-sans mt-0.5">{t.location}</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-stone/60 text-xs" />
                  ))}
                </div>
              </div>
            </div>

            {/* Controls + all cards */}
            <div className="mt-10 pt-8 border-t border-stone/15">
              {/* Mini cards for all testimonials */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8">
                {TESTIMONIALS.map((test, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`px-3 py-2 text-left text-xs font-sans transition-all duration-300 cursor-pointer border ${
                      i === active
                        ? 'bg-charcoal text-cream border-charcoal'
                        : 'bg-transparent text-charcoal/50 border-stone/20 hover:border-stone/50 hover:text-charcoal/70'
                    }`}
                  >
                    <div className="font-medium text-[11px] leading-snug whitespace-nowrap overflow-hidden text-ellipsis">{test.name}</div>
                    <div className={`text-[9px] tracking-wide ${i === active ? 'text-cream/60' : 'text-charcoal/30'}`}>{test.location.split(',')[0]}</div>
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={prev}
                    className="w-10 h-10 flex items-center justify-center border border-charcoal/20 text-charcoal hover:bg-charcoal hover:text-cream transition-all duration-300 cursor-pointer"
                    aria-label="Previous testimonial"
                  >
                    <i className="ri-arrow-left-line text-sm" />
                  </button>
                  <button
                    onClick={next}
                    className="w-10 h-10 flex items-center justify-center border border-charcoal/20 text-charcoal hover:bg-charcoal hover:text-cream transition-all duration-300 cursor-pointer"
                    aria-label="Next testimonial"
                  >
                    <i className="ri-arrow-right-line text-sm" />
                  </button>
                  <span className="text-charcoal/30 text-xs font-sans ml-2">
                    {active + 1} / {TESTIMONIALS.length}
                  </span>
                </div>
                <Link
                  to="/location#contact"
                  className="hidden sm:flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-sans text-stone-dark hover:gap-4 transition-all cursor-pointer whitespace-nowrap"
                >
                  Start Your Project <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
